export const csvDialects = ['excel-de', 'standard'] as const;

export type CsvDialect = (typeof csvDialects)[number];

type CsvDialectConfig = {
  /** Column separator. German Excel expects `;` because `,` is the decimal separator. */
  delimiter: string;
  newline: string;
  /** Byte order mark, required so Excel detects UTF-8 (umlauts, `°`). */
  bom: boolean;
  /** Decimal separator used for numeric columns. */
  decimalSeparator: string;
};

export const csvDialectConfig: Record<CsvDialect, CsvDialectConfig> = {
  'excel-de': { delimiter: ';', newline: '\r\n', bom: true, decimalSeparator: ',' },
  standard: { delimiter: ',', newline: '\n', bom: false, decimalSeparator: '.' },
};

const BOM = '\uFEFF';

/**
 * Spreadsheet applications evaluate cells starting with one of these characters as
 * a formula, so a leading apostrophe is added to keep the value literal.
 */
const formulaPrefixPattern = /^[=+@\t\r]/;
/** A leading `-` is only dangerous when it does not start a number (`-8.5`, `-08°30'00.00"`). */
const negativeNumberPattern = /^-\d/;

const needsFormulaGuard = (value: string): boolean => {
  if (formulaPrefixPattern.test(value)) return true;
  return value.startsWith('-') && !negativeNumberPattern.test(value);
};

export const escapeCsvCell = (value: unknown, delimiter: string): string => {
  const raw = value === null || value === undefined ? '' : String(value);
  const guarded = needsFormulaGuard(raw) ? `'${raw}` : raw;

  const mustQuote =
    guarded.includes(delimiter) ||
    guarded.includes('"') ||
    guarded.includes('\n') ||
    guarded.includes('\r') ||
    guarded !== guarded.trim();

  return mustQuote ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

/**
 * Serializes rows into a CSV file body. The returned string already contains the
 * BOM when the dialect requires one, so it can be written to a file as-is.
 */
export const buildCsv = (rows: Array<Array<unknown>>, dialect: CsvDialect): string => {
  const { delimiter, newline, bom } = csvDialectConfig[dialect];
  const body = rows
    .map((row) => row.map((cell) => escapeCsvCell(cell, delimiter)).join(delimiter))
    .join(newline);

  return `${bom ? BOM : ''}${body}${body ? newline : ''}`;
};

/** Renders a number with the decimal separator the dialect expects. */
export const formatCsvNumber = (
  value: number | null | undefined,
  dialect: CsvDialect,
  fractionDigits?: number
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const text = fractionDigits === undefined ? String(value) : value.toFixed(fractionDigits);
  return text.replace('.', csvDialectConfig[dialect].decimalSeparator);
};

const umlautMap: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  Ä: 'Ae',
  Ö: 'Oe',
  Ü: 'Ue',
  ß: 'ss',
};

const slugifyFileNamePart = (part: string): string => {
  return part
    .replace(/[äöüÄÖÜß]/g, (char) => umlautMap[char] ?? char)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/** Builds a safe `.csv` file name from the given parts, skipping empty ones. */
export const toCsvFileName = (parts: Array<string | null | undefined>): string => {
  const slug = parts
    .map((part) => (part ? slugifyFileNamePart(part) : ''))
    .filter((part) => part !== '')
    .join('-');

  return `${slug || 'export'}.csv`;
};

export const downloadCsvFile = (fileName: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a tick to start the download before releasing the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
